import type { ExcludedAlertState } from '@kirin/api-client/contracts/alerts';

import { formatDateTime, formatUtcDate } from '@/lib/date-utils';

/**
 * Build the three text rows shown in the excluded-alert tooltip from an
 * {@link ExcludedAlertState}. Returns `null` when the alert is not currently
 * excluded so callers can early-return instead of rendering the tooltip.
 *
 * `excludedAt` is a zone-labelled timestamp in `timeZone`
 * ({@link formatDateTime}); `excludedUntil` is a UTC calendar date, labelled
 * UTC when `timeZone` is not ({@link formatUtcDate}), both in the user's date
 * format. Components pass `useDisplayTimeZone()` so the tooltip follows a
 * change. `excludedUntil === null` means an indefinite exclusion and is
 * surfaced as the literal string
 * `"Indefinite"` — the same vocabulary as `formatMutedTooltipLines` so users
 * do not have to learn two terms for the same "no expiry" semantic.
 *
 * The returned object is shape-compatible with `formatMutedTooltipLines`'s
 * output (three string keys: actor, when-applied, when-expires) so the two
 * tooltips can share rendering code if useful.
 */
export function formatExcludedTooltipLines(
  exclude: ExcludedAlertState | null | undefined,
  timeZone: string
): { excluder: string; excludedAt: string; excludedUntil: string } | null {
  if (!exclude?.excluded) return null;
  const excluder =
    exclude.excludedByName ?? exclude.excludedByEmail ?? 'Unknown user';
  const excludedAt = formatDateTime(exclude.excludedAt, timeZone);
  const excludedUntil = exclude.excludedUntil
    ? formatUtcDate(exclude.excludedUntil, timeZone !== 'UTC')
    : 'Indefinite';
  return { excluder, excludedAt, excludedUntil };
}
